// =============================================================================
// CreateProjectDialog - Dialog de création de projet (ClickUp-like)
// =============================================================================

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { FolderKanban, Loader2, X, Camera, FileText, CalendarIcon, ChevronDown, Search, UserPlus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { createProjectViaEdgeFunction, uploadNewProjectLogo } from '@/modules/project/data/createProjectRepository';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { useSidebarProjectsStore } from '@/modules/project/hooks';
import { fetchOrganizationMembers } from '@/modules/core/data/memberRepository';
import type { OrganizationMemberWithProfile } from '@/modules/core/types';

interface CreateProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultSpaceId?: string | null;
}

export function CreateProjectDialog({ open, onOpenChange, defaultSpaceId }: CreateProjectDialogProps) {
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();
  const { loadSidebarData } = useSidebarProjectsStore();
  
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [dueDate, setDueDate] = useState<Date | undefined>(undefined);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [spaceId, setSpaceId] = useState<string | null>(null);
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [dueDateOpen, setDueDateOpen] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);

  // Share members state
  const [sharedMemberIds, setSharedMemberIds] = useState<string[]>([]);
  const [orgMembers, setOrgMembers] = useState<OrganizationMemberWithProfile[]>([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [sharePopoverOpen, setSharePopoverOpen] = useState(false);

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSpaceId(defaultSpaceId ?? null);
      setName('');
      setDescription('');
      setStartDate(undefined);
      setDueDate(undefined);
      setLogoFile(null);
      setIsPrivate(false);
      setSharedMemberIds([]);
      setMemberSearch('');
    }
  }, [open, defaultSpaceId]);

  // Load org members when private mode is activated
  useEffect(() => {
    if (isPrivate && currentOrganization && orgMembers.length === 0) {
      fetchOrganizationMembers(currentOrganization.id).then(setOrgMembers);
    }
  }, [isPrivate, currentOrganization, orgMembers.length]);

  // Clear shared members when switching back to public
  useEffect(() => {
    if (!isPrivate) {
      setSharedMemberIds([]);
    }
  }, [isPrivate]);

  // Generate preview URL for selected logo
  const logoPreviewUrl = useMemo(() => {
    if (logoFile) {
      return URL.createObjectURL(logoFile);
    }
    return null;
  }, [logoFile]);

  const getInitials = (n: string) => {
    return n.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  // Members available for sharing (exclude current user)
  const availableMembers = useMemo(() => {
    return orgMembers.filter(m => m.user_id !== user?.id);
  }, [orgMembers, user?.id]);

  // Filter members by search
  const filteredMembers = useMemo(() => {
    if (!memberSearch.trim()) return availableMembers;
    const q = memberSearch.toLowerCase();
    return availableMembers.filter(m =>
      m.profile?.full_name?.toLowerCase().includes(q) ||
      m.profile?.email?.toLowerCase().includes(q)
    );
  }, [availableMembers, memberSearch]);

  // Selected members for display
  const selectedMembers = useMemo(() => {
    return orgMembers.filter(m => sharedMemberIds.includes(m.id));
  }, [orgMembers, sharedMemberIds]);

  const toggleMember = useCallback((memberId: string) => {
    setSharedMemberIds(prev =>
      prev.includes(memberId) ? prev.filter(id => id !== memberId) : [...prev, memberId]
    );
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Format non supporté (JPEG, PNG, WebP ou GIF)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La taille maximale est de 5 Mo');
      return;
    }
    setLogoFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const dateError = useMemo(() => {
    if (startDate && dueDate && startDate > dueDate) {
      return 'La date de début ne peut pas être postérieure à la date de fin';
    }
    return null;
  }, [startDate, dueDate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentOrganization) {
      toast.error('Aucun workspace sélectionné');
      return;
    }
    
    if (!name.trim() || name.trim().length < 2) {
      toast.error('Le nom du projet doit contenir au moins 2 caractères');
      return;
    }

    if (dateError) {
      toast.error(dateError);
      return;
    }

    setIsLoading(true);

    try {
      const formatDate = (d: Date | undefined) => d ? format(d, 'yyyy-MM-dd') : null;

      const project = await createProjectViaEdgeFunction({
        orgId: currentOrganization.id,
        name: name.trim(),
        description: description.trim() || null,
        startDate: formatDate(startDate),
        dueDate: formatDate(dueDate),
        spaceId: spaceId || null,
        visibility: isPrivate ? 'private' : 'public',
        sharedMemberIds: isPrivate ? sharedMemberIds : [],
      });

      const projectId = project.id;

      // Upload logo if selected
      if (logoFile && projectId) {
        try {
          await uploadNewProjectLogo(currentOrganization.id, projectId, logoFile);
        } catch (logoError) {
          console.error('Error uploading logo:', logoError);
        }
      }

      toast.success('Projet créé avec succès');
      onOpenChange(false);
      loadSidebarData(currentOrganization.id);
      
      if (projectId) {
        navigate(`/projects/${projectId}`);
      }
    } catch (err: any) {
      console.error('Error creating project:', err);
      toast.error(err.message || 'Erreur lors de la création du projet');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!isLoading) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-[560px] w-[95vw] p-0 gap-0 overflow-visible [&>button[class*='absolute']]:hidden rounded-xl">
        <VisuallyHidden>
          <DialogTitle>Créer un nouveau projet</DialogTitle>
          <DialogDescription>Renseignez les informations du projet à créer.</DialogDescription>
        </VisuallyHidden>
        <form onSubmit={handleSubmit} className="flex flex-col" data-testid="create-project-dialog">
          {/* Close button */}
          <button
            type="button"
            onClick={() => { if (!isLoading) onOpenChange(false); }}
            className="absolute right-3 top-3 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors z-10"
          >
            <X className="h-4 w-4" />
          </button>

          {/* Header bar */}
          <div className="px-5 pt-4 pb-2 flex items-center gap-2">
            <div className="inline-flex items-center gap-1.5 h-7 px-2.5 text-xs font-medium rounded-md border border-border bg-background">
              <FolderKanban className="h-3 w-3" style={{ color: 'hsl(var(--module-primary))' }} />
              <span className="text-muted-foreground">Nouveau projet</span>
            </div>
          </div>

          {/* Logo + Title */}
          <div className="px-5 pb-2 pt-1 flex items-start gap-3">
            {/* Compact logo picker */}
            <div
              className="relative group cursor-pointer shrink-0 mt-0.5"
              onClick={() => fileInputRef.current?.click()}
            >
              <Avatar className="h-10 w-10 border border-border">
                <AvatarImage src={logoPreviewUrl || undefined} />
                <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/10 text-xs font-semibold">
                  {name ? getInitials(name) : <FolderKanban className="h-4 w-4 text-primary" />}
                </AvatarFallback>
              </Avatar>
              <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Camera className="h-3.5 w-3.5 text-white" />
              </div>
              {logoFile && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setLogoFile(null); }}
                  className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center shadow-md hover:bg-destructive/90 transition-colors"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleFileSelect}
              className="hidden"
            />

            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Nom du projet"
              autoFocus
              disabled={isLoading}
              className="w-full bg-transparent border-0 outline-none text-xl font-medium placeholder:text-muted-foreground/40 text-foreground"
              data-testid="create-project-name-input"
            />
          </div>

          {/* Description */}
          <div className="px-5 pb-3">
            <div className="flex gap-2 text-muted-foreground/50">
              <FileText className="h-3.5 w-3.5 shrink-0 mt-1" />
              <textarea
                value={description}
                onChange={e => {
                  setDescription(e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = e.target.scrollHeight + 'px';
                }}
                placeholder="Ajouter une description"
                rows={1}
                disabled={isLoading}
                className="w-full bg-transparent border-0 outline-none text-sm placeholder:text-muted-foreground/40 text-foreground resize-none overflow-hidden"
              />
            </div>
          </div>

          {/* Chips row - Dates */}
          <div className="px-5 py-4 flex items-center gap-1.5 flex-wrap border-t border-border/40">
            {/* Start date chip */}
            <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  disabled={isLoading}
                  className="inline-flex items-center gap-1.5 h-7 px-2.5 text-xs font-medium rounded-md border border-border bg-background text-muted-foreground hover:bg-muted/60 transition-colors cursor-pointer"
                >
                  <CalendarIcon className="h-3 w-3" />
                  {startDate ? format(startDate, 'd MMM yyyy', { locale: fr }) : 'Date de début'}
                  {startDate && (
                    <span
                      className="ml-0.5 hover:text-destructive"
                      onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      onClick={(e) => { e.stopPropagation(); e.preventDefault(); setStartDate(undefined); setDueDate(undefined); setStartDateOpen(false); }}
                    >
                      <X className="h-2.5 w-2.5" />
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={(d) => {
                    setStartDate(d);
                    setStartDateOpen(false);
                    // Clear due date if it's now before the new start date
                    if (d && dueDate && dueDate < d) setDueDate(undefined);
                  }}
                  locale={fr}
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>

            {/* Due date chip - disabled until start date is chosen */}
            <Popover open={dueDateOpen} onOpenChange={(v) => { if (startDate) setDueDateOpen(v); }}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  disabled={isLoading || !startDate}
                  className={cn(
                    "inline-flex items-center gap-1.5 h-7 px-2.5 text-xs font-medium rounded-md border border-border bg-background text-muted-foreground transition-colors",
                    startDate ? "hover:bg-muted/60 cursor-pointer" : "opacity-50 cursor-not-allowed"
                  )}
                >
                  <CalendarIcon className="h-3 w-3" />
                  {dueDate ? format(dueDate, 'd MMM yyyy', { locale: fr }) : 'Date de fin'}
                  {dueDate && (
                    <span
                      className="ml-0.5 hover:text-destructive"
                      onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      onClick={(e) => { e.stopPropagation(); e.preventDefault(); setDueDate(undefined); setDueDateOpen(false); }}
                    >
                      <X className="h-2.5 w-2.5" />
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dueDate}
                  onSelect={(d) => { setDueDate(d); setDueDateOpen(false); }}
                  locale={fr}
                  disabled={startDate ? (date) => date < startDate : undefined}
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Private toggle - ClickUp style */}
          <div className="px-5 py-3 border-t border-border/40">
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-foreground">Rendre privé</span>
                <span className="text-xs text-muted-foreground">
                  Uniquement vous et les membres invités y ont accès.
                </span>
              </div>
              <Switch
                checked={isPrivate}
                onCheckedChange={setIsPrivate}
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Share with members - ClickUp style (only visible when private) */}
          {isPrivate && (
            <div className="px-5 py-3 border-t border-border/40">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Partager uniquement avec</span>
                <div className="flex items-center gap-1">
                  {/* Selected member avatars */}
                  {selectedMembers.slice(0, 5).map(m => (
                    <Avatar key={m.id} className="h-7 w-7 border-2 border-background -ml-1 first:ml-0">
                      <AvatarImage src={m.profile?.avatar_url || undefined} />
                      <AvatarFallback className="text-[10px] font-medium bg-muted">
                        {getInitials(m.profile?.full_name || m.profile?.email || '?')}
                      </AvatarFallback>
                    </Avatar>
                  ))}
                  {selectedMembers.length > 5 && (
                    <span className="text-xs text-muted-foreground ml-1">+{selectedMembers.length - 5}</span>
                  )}

                  {/* Add member button / popover */}
                  <Popover open={sharePopoverOpen} onOpenChange={setSharePopoverOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="h-7 w-7 rounded-full border border-dashed border-border flex items-center justify-center text-muted-foreground hover:bg-muted/60 hover:text-foreground transition-colors -ml-1"
                      >
                        <UserPlus className="h-3.5 w-3.5" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-0" align="end" sideOffset={8}>
                      {/* Search input */}
                      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border">
                        <Search className="h-4 w-4 text-muted-foreground shrink-0" />
                        <input
                          value={memberSearch}
                          onChange={e => setMemberSearch(e.target.value)}
                          placeholder="Rechercher un membre..."
                          className="w-full bg-transparent border-0 outline-none text-sm placeholder:text-muted-foreground/50 text-foreground"
                          autoFocus
                        />
                      </div>

                      {/* Members list */}
                      <div className="max-h-52 overflow-y-auto py-1">
                        {filteredMembers.length === 0 ? (
                          <div className="px-3 py-4 text-xs text-muted-foreground text-center">
                            Aucun membre trouvé
                          </div>
                        ) : (
                          <>
                            <div className="px-3 pt-1.5 pb-1 text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wider">
                              Personnes
                            </div>
                            {filteredMembers.map(m => {
                              const isSelected = sharedMemberIds.includes(m.id);
                              return (
                                <button
                                  key={m.id}
                                  type="button"
                                  onClick={() => toggleMember(m.id)}
                                  className={cn(
                                    "w-full flex items-center gap-2.5 px-3 py-1.5 text-left hover:bg-muted/60 transition-colors",
                                    isSelected && "bg-primary/5"
                                  )}
                                >
                                  <div className="relative">
                                    <Avatar className={cn(
                                      "h-7 w-7",
                                      isSelected && "ring-2 ring-primary ring-offset-1 ring-offset-background"
                                    )}>
                                      <AvatarImage src={m.profile?.avatar_url || undefined} />
                                      <AvatarFallback className="text-[10px] font-medium bg-muted">
                                        {getInitials(m.profile?.full_name || m.profile?.email || '?')}
                                      </AvatarFallback>
                                    </Avatar>
                                  </div>
                                  <span className="text-sm text-foreground truncate">
                                    {m.profile?.full_name || m.profile?.email}
                                  </span>
                                </button>
                              );
                            })}
                          </>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="px-5 py-3 border-t border-border/60 flex items-center justify-end gap-2 bg-muted/30">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => { if (!isLoading) onOpenChange(false); }}
              disabled={isLoading}
              className="text-xs"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={isLoading || !name.trim() || name.trim().length < 2 || !!dateError}
              className="text-white text-xs font-semibold h-9 px-5 rounded-lg gap-1.5"
              style={{ backgroundColor: 'hsl(var(--module-primary))' }}
              data-testid="create-project-submit"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Création...
                </>
              ) : (
                <>
                  Créer le projet
                  <ChevronDown className="h-3 w-3 opacity-70" />
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
